import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppThunk } from "@Store/index";
import axios from "axios";
import { getMetaSuccess } from "@App/appReducer";
import config from "@Config/index";
import getBlogCollectionUrl, {
  type RegionalBlogLanguage,
} from "@Pages/Blog/utils/getBlogCollectionUrl";
import getNewBlogPostSchema from "./newBlogPostSchema";

export interface NewBlogPostState {
  readyStatus: "invalid" | "request" | "success" | "failure";
  blogData: any;
  isDraft: boolean;
  slug: string;
  isGone: boolean;
}

const initialState: NewBlogPostState = {
  readyStatus: "invalid",
  blogData: null,
  isDraft: false,
  slug: "",
  isGone: false,
};

const newBlogPostSlice = createSlice({
  name: "newBlogPostData",
  initialState,
  reducers: {
    setRequest: (state) => {
      state.readyStatus = "request";
    },
    setSuccess: (
      state,
      { payload }: PayloadAction<{ blogData: any; isDraft: boolean; slug: string }>,
    ) => {
      state.readyStatus = "success";
      state.blogData = payload.blogData;
      state.isDraft = payload.isDraft;
      state.slug = payload.slug;
      state.isGone = false;
    },
    setFailure: (
      state,
      { payload }: PayloadAction<{ slug?: string; isGone?: boolean } | undefined>,
    ) => {
      state.readyStatus = "failure";
      state.blogData = null;
      state.slug = payload?.slug || "";
      state.isGone = !!payload?.isGone;
    },
  },
});

const { setRequest, setSuccess, setFailure } = newBlogPostSlice.actions;
export default newBlogPostSlice.reducer;

// Resolves the current request path on both server (props.req) and client.
const getRequestPath = (props: any): string =>
  __SERVER__ ? props?.req?.path || "" : window.location.pathname;

// Resolves the article slug: prefer the matched route param, fall back to the
// last non-empty path segment.
const getSlug = (props: any): string => {
  const paramSlug = props?.params?.slug || props?.match?.params?.slug;
  if (paramSlug) return paramSlug;
  const path = getRequestPath(props);
  return path.split("/").filter(Boolean).pop() || "";
};

/**
 * SSR/CSR data loader for the new blog post page. Fetches the `blog-articles`
 * doc for the current slug, sets SEO meta, and marks a 404 on the server when
 * the article is not found.
 */
export const fetchNewBlogPostData =
  (props: any, lang?: RegionalBlogLanguage): AppThunk =>
  async (dispatch, getState) => {
    const slug = getSlug(props);
    const path = getRequestPath(props);
    const isDraft = path.startsWith("/drafts-blog/");

    if (!slug) {
      dispatch(setFailure());
      if (props?.staticContext) props.staticContext.statusCode = 404;
      return;
    }

    // Skip re-fetch if we already have this slug loaded.
    const current = (getState() as any).newBlogPostData;
    // if (current?.slug === slug && current?.readyStatus === "success") {
    if (
      current?.slug === slug &&
      (current?.readyStatus === "success" || current?.readyStatus === "failure")
    ) {
      return;
    }

    dispatch(setRequest());

    if (!isDraft && config.CMS_API_URL) {
      try {
        const editorialRes = await axios.get(`${config.CMS_API_URL}/api/editorial-page-data`);
        const editorial = editorialRes.data?.data;
        const editorialSlug = editorial?.urlSlug?.trim();
        if (editorialSlug && slug === editorialSlug && editorial?.postBody?.root) {
          const canonicalPath = editorial.canonicalPath?.trim() || `/blog/${editorialSlug}`;
          if (props?.staticContext) {
            const query = props.req?.originalUrl?.includes("?")
              ? props.req.originalUrl.slice(props.req.originalUrl.indexOf("?"))
              : "";
            props.staticContext.url = `${lang ? `/${lang}` : ""}${canonicalPath}${query}`;
          }
          dispatch(setFailure({ slug }));
          return;
        }
      } catch {
        /* editorial singleton optional */
      }
    }

    const statusFilter = isDraft ? "draft" : "published";
    const baseUrl = getBlogCollectionUrl("blog-articles", lang);
    const apiUrl = `${baseUrl}?where[slug][equals]=${encodeURIComponent(
      slug,
    )}&where[_status][equals]=${statusFilter}&where[isArchived][equals]=false&depth=2`;

    try {
      const res = await axios.get(apiUrl);
      const docs = res.data?.docs || [];
      const blogData = docs[0];

      if (!blogData) {
        if (props?.staticContext) {
          const isEnglishPost = !lang && !isDraft && !config.IS_CHINA_APP;
          const legacy = isEnglishPost
            ? await axios
                .get(
                  `${getBlogCollectionUrl("blog-posts")}?where[slug][equals]=${encodeURIComponent(
                    slug,
                  )}&limit=1&depth=0&select[id]=true`,
                )
                .catch(() => null)
            : null;
          const isGone = !!legacy?.data?.docs?.length;
          dispatch(setFailure({ slug, isGone }));
          props.staticContext.statusCode = isGone ? 410 : 404;
          delete props.staticContext.url;
        } else {
          dispatch(setFailure({ slug }));
        }
        return;
      }

      if (
        props?.staticContext &&
        /^(\/[a-z]{2}(?:-[a-z]{2})?)?\/blog\/post\/[^/]+\/?$/i.test(path)
      ) {
        const query = props.req?.originalUrl?.slice(path.length) || "";
        props.staticContext.url = `${lang ? `/${lang}` : ""}/blog/${slug}${query}`;
        return;
      }

      // SEO meta for the article.
      const meta: any = {};
      meta.title = blogData.metaTitle || blogData.name;
      meta.description = blogData.metaDescription || "";
      if (blogData.metaKeywords) meta.keywords = blogData.metaKeywords;
      meta.canonical_url_suffix = lang ? `/${lang}/blog/${slug}` : `/blog/${slug}`;
      meta.schemaData = getNewBlogPostSchema(blogData, slug, lang);
      meta.excludeSiteSchema = true;
      dispatch(getMetaSuccess(meta));

      dispatch(setSuccess({ blogData, isDraft, slug }));
    } catch {
      dispatch(setFailure());
      if (props?.staticContext) props.staticContext.statusCode = 500;
    }
  };
